// Mukti Hospital ERP - Pathology Reference Pre-loader
//

import axios from 'axios';
import { saveToCache } from './pathologyCache';

const API_BASE = 'https://api.muktihospital.com/api';
const API_KEY =
  'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';

const HEADERS = { 'x-api-key': API_KEY };

// Each entry: [url, cacheKey, pickFromResponse]
const TARGETS = [
  // Tests — used by the bill form's test picker AND by the test list.
  [
    `${API_BASE}/pathology`,
    'pathology_tests',
    (data) =>
      Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [],
  ],
  // Categories — required by the Add Test modal dropdown.
  [
    `${API_BASE}/pathology-category`,
    'pathology_categories',
    (data) => (Array.isArray(data?.categories) ? data.categories : []),
  ],
  // Agents — used by the bill form's agent picker AND by the agent list.
  [
    `${API_BASE}/pathology-agents`,
    'pathology_agents',
    (data) => {
      const arr = Array.isArray(data?.agents) ? data.agents : [];
      // Match the shape PathologyAgentList saves.
      return arr.map((agent) => ({
        id: agent.id,
        agentCode: agent.agentCode || '',
        name: agent.agentName || '',
        designation: agent.designation || '',
        contactNo: agent.contactNumber || 'N/A',
        address: agent.address || '',
        division: agent.division || 'Dhaka',
        district: agent.district || 'Dhaka',
        upazila: agent.upazila || '',
        union: agent.union || '',
      }));
    },
  ],
];

const fetchOne = async ([url, key, pick]) => {
  try {
    const res = await axios.get(url, { headers: HEADERS, timeout: 15000 });
    const list = pick(res.data) || [];
    if (Array.isArray(list) && list.length > 0) {
      await saveToCache(key, list);
    }
  } catch (err) {
  }
};

/**
 * Warm Pathology Account Summary cache for the filter combinations the user
 * is most likely to look at. Each combination is fetched in parallel and
 * written to the SAME cache key the Account Summary page uses, so going to
 * any of these views offline returns instantly with no fetch.
 *
 * Coverage:
 *   - monthly: current month + last 2 months (recent business activity)
 *   - yearly: current year + last year (comparison)
 *   - daily: today (today's collection)
 *
 * All errors are swallowed individually — one slow combination doesn't
 * block the others.
 */
const preloadAccountSummary = async () => {
  const now = new Date();
  const pad2 = (n) => String(n).padStart(2, '0');
  const ymd = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  // Build last 3 monthly filters (current + 2 previous).
  const monthlyFilters = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthlyFilters.push({
      type: 'monthly',
      year: String(d.getFullYear()),
      month: pad2(d.getMonth() + 1),
    });
  }

  // Build last 2 yearly filters (current + previous).
  const yearlyFilters = [
    { type: 'yearly', year: String(now.getFullYear()) },
    { type: 'yearly', year: String(now.getFullYear() - 1) },
  ];

  // Daily: today (single day range).
  const today = ymd(now);
  const dailyFilters = [
    { type: 'daily', startDate: today, endDate: today },
  ];

  const allFilters = [...monthlyFilters, ...yearlyFilters, ...dailyFilters];

  const fetchSummaryFor = async (params) => {
    try {
      const cacheKey = `pathology_account_summary_${JSON.stringify(params)}`;
      const res = await axios.get(
        `${API_BASE}/pathology-accounts/account-summary`,
        { headers: HEADERS, params, timeout: 20000 }
      );
      const payload = {
        bills: res.data?.bills || [],
        summary: res.data?.summary || null,
      };
      await saveToCache(cacheKey, payload);
    } catch (err) {
      // Best-effort only; leave any prior cache intact.
    }
  };

await Promise.all(allFilters.map(fetchSummaryFor));
};

/**
 * Warm the BULK RECENT cache: a single fetch returns all raw pathology bills
 * and agent withdrawals for the last 3 months. The Account Summary page can
 * then compute the summary for ANY filter combination inside that window
 * entirely client-side, even fully offline.
 *
 * Cache key: `pathology_account_summary_bulk_recent`
 *
 * This is the offline-emergency safety net — we may not have pre-fetched
 * the user's exact filter combination, but if it falls inside the 3-month
 * window we can still serve it.
 */
export const preloadAccountSummaryBulk = async () => {
  try {
    const res = await axios.get(
      `${API_BASE}/pathology-accounts/bulk-recent`,
      { headers: HEADERS, timeout: 30000 }
    );
    const payload = {
      windowStart: res.data?.windowStart || null,
      windowEnd: res.data?.windowEnd || null,
      bills: res.data?.bills || [],
      withdrawals: res.data?.withdrawals || [],
      doctorWithdrawals: res.data?.doctorWithdrawals || [],
      cachedAt: new Date().toISOString(),
    };
    await saveToCache('pathology_account_summary_bulk_recent', payload);
  } catch (err) {
    // Best-effort only; leave any prior bulk cache intact.
  }
};

/**
 * Warm all pathology reference caches in parallel. Safe to call repeatedly;
 * later runs just refresh the cache with newer data.
 */
export const preloadPathologyRefs = async () => {
  if (!navigator.onLine) return;
  await Promise.all([
    ...TARGETS.map(fetchOne),
    preloadAccountSummary(),
    preloadAccountSummaryBulk(),
  ]);
};

export default preloadPathologyRefs;
